import { Alert, Card, Flex, Image, Space, Typography } from 'antd';
import type { EnmonMeterPhotoLink } from '@/features/inventorization/enmon-meter-files-client';
import { useReviewPhotoUrls } from '@/features/inventorization/photos/useReviewPhotos';
import { reviewText } from '@/features/inventorization/review-i18n';
import type { LocalPhotoRef } from '@/shared/capture/capture-session';

export function PhotoReviewGallery({
  isLoadingRemote = false,
  photos,
  remoteError,
  remotePhotos = [],
}: {
  isLoadingRemote?: boolean;
  photos: LocalPhotoRef[];
  remoteError?: string | null;
  remotePhotos?: EnmonMeterPhotoLink[];
}) {
  const urls = useReviewPhotoUrls(photos);

  if (photos.length === 0 && remotePhotos.length === 0 && !isLoadingRemote && !remoteError) {
    return <Typography.Text type="secondary">{reviewText('photos.none', 'No photos captured yet.')}</Typography.Text>;
  }

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {remoteError ? <Alert closable message={remoteError} showIcon type="warning" /> : null}
      {remotePhotos.length > 0 || isLoadingRemote ? (
        <Space direction="vertical" size="small">
          <Typography.Text strong>{reviewText('photos.existing', 'Existing photos')}</Typography.Text>
          <Flex gap="small" wrap>
            {remotePhotos.map(photo => (
              <Card key={photo.fileId} size="small" styles={{ body: { padding: 6 } }}>
                <Image alt={photo.label} height={144} src={photo.link} style={{ objectFit: 'cover' }} width={144} />
                <Typography.Text style={{ display: 'block', maxWidth: 144 }} type="secondary">
                  {reviewText('photos.uploadedLabel', 'Uploaded · {label}', { label: photo.label })}
                </Typography.Text>
              </Card>
            ))}
            {isLoadingRemote ? (
              <Card size="small" styles={{ body: { padding: 6 } }}>
                <Flex align="center" justify="center" style={{ height: 144, width: 144 }}>
                  <Typography.Text type="secondary">
                    {reviewText('photos.loadingUploaded', 'Loading uploaded photos')}
                  </Typography.Text>
                </Flex>
              </Card>
            ) : null}
          </Flex>
        </Space>
      ) : null}
      {photos.length > 0 ? (
        <Space direction="vertical" size="small">
          <Typography.Text strong>{reviewText('photos.new', 'New photos')}</Typography.Text>
          <Flex gap="small" wrap>
            {photos.map(photo => (
              <LocalPhotoCard key={photo.id} photo={photo} url={urls[photo.id]} />
            ))}
          </Flex>
        </Space>
      ) : null}
    </Space>
  );
}

function LocalPhotoCard({ photo, url }: { photo: LocalPhotoRef; url: string | undefined }) {
  return (
    <Card size="small" styles={{ body: { padding: 6 } }}>
      {url ? (
        <Image
          alt={reviewText('photos.localAlt', '{context} photo', { context: photo.context })}
          height={144}
          src={url}
          style={{ objectFit: 'cover' }}
          width={144}
        />
      ) : (
        <Flex align="center" justify="center" style={{ height: 144, width: 144 }}>
          <Typography.Text type="secondary">{reviewText('photos.loading', 'Loading')}</Typography.Text>
        </Flex>
      )}
      <Typography.Text style={{ display: 'block', maxWidth: 144 }} type="secondary">
        {photo.registerId ? `${photo.context} · ${photo.registerId}` : photo.context}
      </Typography.Text>
    </Card>
  );
}
